<template>
  <div>
    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      :enable-row-clickable="true"
      @newDocument="newDocument"
      @reloadData="reloadDataFunc"
      @clickedRow="openEdit($event)"
      @openMultiUserDialog="openMultiUserDialog"
      @openBulkMove="openBulkMove"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <moveDialog
      :item="bulkMove.item"
      :moveDialog="bulkMove.dialog"
      :loading="bulkMove.loading"
      :array="bulkMove.targets"
      @cancel="bulkMove.dialog.open = false"
      @confirm="handleBulkMoveConfirm"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bulkConfirm.open = false" :disabled="bulkConfirm.loading">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="bulkConfirm.loading" @click="runBulkDelete">
            {{ $t("proof.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="proof"
      :availableItems="transferItems"
      itemText="name"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.proofs"
    />
  </div>
</template>

<script>
import { getUniqueId } from "../../components/api/api";
import tableComponent from "../../components/_utils/dataTableHelpers/tableComponent.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";

export default {
  components: { tableComponent, multiUserDialog, moveDialog },
  data: () => ({
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      multipleSelectButtons: [
        {
          title: "proof.bulk.moveButton",
          color: "primary",
          action: "openBulkMove",
          icon: "content_paste_go",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "proof.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "proof.addProof",
          color: "secondary",
          action: "newDocument",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    bulkMove: {
      item: {},
      loading: false,
      targets: [],
      ids: [],
      dialog: {
        open: false,
        title: "proof.bulk.moveTitle",
        description: "",
      },
    },
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      ids: [],
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
  }),
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    const res = await this.$store.dispatch("getAllProofs");
    this.transferItems = res?.data || res || [];

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons = this.tableSettings.headerButtons.filter(
        (btn) => btn.action !== "openMultiUserDialog",
      );
    }

    this.$root.$on("reload-proof-table", this.reloadDataFunc);
  },
  beforeDestroy() {
    this.$root.$off("reload-proof-table", this.reloadDataFunc);
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("proof.tableHeader.status"),
          value: "proofStatus",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("proof.tableHeader.name"),
          value: "name",
          align: "left",
        },
        {
          text: this.$t("proof.tableHeader.identifier"),
          value: "identifier",
        },
        {
          text: this.$t("proof.tableHeader.category"),
          value: "category.name",
        },
        {
          text: this.$t("proof.tableHeader.nextProofDate"),
          value: "proofNextDate",
          sortable: false,
        },
        {
          text: this.$t("proof.tableHeader.interval"),
          value: "interval",
        },
        {
          text: this.$t("proof.tableHeader.costs"),
          value: "proofCosts",
        },
        {
          text: this.$t("proof.tableHeader.tags"),
          value: "proofTags",
          sortable: false,
        },
        {
          text: "",
          value: "proofAction",
          align: "right",
          sortable: false,
        },
      ];
    },
    tableData() {
      return {
        tableComponentSlots: [
          {
            id: getUniqueId(),
            slotName: "item.proofStatus",
            componentName: "proofStatus",
          },
          {
            id: getUniqueId(),
            slotName: "item.proofNextDate",
            componentName: "proofNextDate",
          },
          {
            id: getUniqueId(),
            slotName: "item.proofCosts",
            componentName: "proofCosts",
          },
          {
            id: getUniqueId(),
            slotName: "item.proofTags",
            componentName: "proofTags",
          },
          {
            id: getUniqueId(),
            slotName: "item.proofAction",
            componentName: "proofAction",
          },
        ],
        itemKey: "_id",
        uriPartPrepend: "proof",
        uriPartAppend: this.$route.params.proofCategoryId
          ? `&category=${this.$route.params.proofCategoryId}&populate=category,tags`
          : "&populate=category,tags",
        populate: [],
        sort: "nextProofDate",
        editFormPushName: "proofForm",
      };
    },
  },
  methods: {
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
    },
    async openBulkMove(selectedItems) {
      this.bulkMove.ids = (selectedItems || []).map((i) => i._id);
      const res = await this.$store.dispatch("getAllProofCategories");
      const cats = (res && res.data) || res || [];
      this.bulkMove.targets = cats
        .map((c) => ({ id: c.id || c._id, name: c.name }))
        .filter(
          (t) =>
            String(t.id) !== String(this.$route.params.proofCategoryId),
        );
      this.bulkMove.dialog.description = this.$t("proof.bulk.moveText", {
        count: this.bulkMove.ids.length,
      });
      this.bulkMove.dialog.open = true;
    },
    async handleBulkMoveConfirm(targetId) {
      try {
        this.bulkMove.loading = true;
        await this.$store.dispatch("bulkMoveProofs", {
          ids: this.bulkMove.ids,
          category: targetId,
        });
        this.bulkMove.dialog.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkMove.loading = false;
      }
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((i) => i._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("proof.bulk.deleteTitle"),
        message: this.$t("proof.bulk.deleteConfirm", { count: ids.length }),
        ids,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch("bulkDeleteProofs", this.bulkConfirm.ids);
        this.bulkConfirm.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    onBulkActionDone() {
      if (this.$refs.tableComponent) {
        this.$refs.tableComponent.clearSelectItems();
      }
      this.reloadDataFunc();
    },
    newDocument() {
      this.$router.push({
        name: "proofForm",
        params: { id: "1", mode: "new" },
      });
    },
    openEdit(proof) {
      this.$router.push({
        name: "proofForm",
        params: { id: proof._id || proof.id, mode: "edit" },
      });
    },
    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },
    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);
        const parts = [];
        if (result.totalSuccess > 0)
          parts.push(this.$t("multiUser.result.success", { number: result.totalSuccess }));
        if (result.totalConflict > 0)
          parts.push(this.$t("multiUser.result.error", { number: result.totalConflict }));
        if (result.totalError > 0)
          parts.push(this.$t("multiUser.result.error", { number: result.totalError }));
        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));
        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
  },
};
</script>
